import React, { useEffect, useState } from "react";
import { Card, Typography, Space, Modal, Spin, Divider, Empty } from "antd";
import MenuLayout from "../../components/Menu";
import { supabase } from "../../lib/supabase";
import ProfileCard from "../alum-map/ProfileCard";
import Loading from "../../components/Loading";
import { getJobFieldName } from "../../utils/fieldMapper";
import { 
  useAlumniCards, 
  formatSchoolInfo, 
  generateCardId,
  formatCareerInfo,
  formatEducationInfo,
  isCareerInfoEmpty,
  isEducationInfoEmpty
} from "../../utils/alumniCardUtils";
import "../../styles/alumni-cards.css";
import "./index.css";

const { Title, Paragraph, Text } = Typography;
const { Meta } = Card;

const AboutPage = () => {
  const [devTeamMembers, setDevTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedContact, setExpandedContact] = useState(null);
  const { cardRefs, handleMouseMove, handleMouseLeave, setCardRef } = useAlumniCards();

  // Function to fetch dev team members using the same API as search page
  const fetchDevTeamMembers = async () => {
    setLoading(true);
    try {
      // Query for 2011级 class 1 members
      // The query_filter function expects all parameters in order: q_name, q_loc, q_schoolm_start_time, q_schoolm_class, q_schoolh_start_time, q_schoolh_class, q_school, q_major, q_career, q_job, qsize, page
      const { data, error } = await supabase.rpc("query_filter", {
        q_name: null,
        q_loc: null,
        q_schoolm_start_time: 2011,
        q_schoolm_class: 1,
        q_schoolh_start_time: null,
        q_schoolh_class: null,
        q_school: null,
        q_major: null,
        q_career: null,
        q_job: null,
        qsize: 50, // Get more results to ensure we find our target members
        page: 1
      });

      if (error) {
        console.error("Error fetching dev team members:", error);
        return;
      }

      const alumniList = data?.data || [];
      
      // Filter for the specific dev team members and maintain order
      const targetNames = ["朱星宇", "姚钧元", "麦天承"];
      const filteredMembers = targetNames
        .map(name => alumniList.find(member => member.name_cn === name))
        .filter(member => member !== undefined);

      setDevTeamMembers(filteredMembers);
    } catch (error) {
      console.error("Error in fetchDevTeamMembers:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mouse tracking functions are now imported from shared utility

  // Handle clicking on a profile card
  const handleClickCard = (member) => {
    setExpandedContact(member);
  };

  useEffect(() => {
    fetchDevTeamMembers();
  }, []);

  return (
    <MenuLayout>
      <div className="about-container">
        <div className="about-header">
          <Space direction="vertical" size="middle" className="header-content">
            <Title level={1} className="about-title">
              关于校友平台
            </Title>
            <Paragraph className="about-subtitle">
              欢迎参与EDP校友平台内测
            </Paragraph>
          </Space>
        </div>

        <div className="about-content">
          <Card className="mission-card">
            <Title level={2} className="section-title">
              平台介绍
            </Title>
            <Paragraph className="mission-text">
              EDP校友平台致力于为校友们提供一个夸届连接的平台。通过地图展示校友分布，
              个人资料管理，以及搜索功能，让校友们能够更好地保持联系，分享经验，共同成长。
            </Paragraph>
          </Card>

          <Card className="timeline-card">
            <Title level={2} className="section-title">
              开发团队
            </Title>
            <Paragraph className="mission-text">
              我们的开发团队由2011级1班的三位校友组成:
            </Paragraph>
            
            <Spin tip="加载中..." indicator={Loading} spinning={loading}>
              <div className="alumni-list">
                {devTeamMembers.length > 0 ? (
                  devTeamMembers.map((member, index) => {
                    const cardId = generateCardId('dev-card', index);
                    
                    return (
                      <Card
                        key={member.id || `dev-member-${index}`}
                        ref={(el) => setCardRef(cardId, el)}
                        bordered={false}
                        className="alumni-card"
                        onMouseMove={(e) => handleMouseMove(e, cardId)}
                        onMouseLeave={() => handleMouseLeave(cardId)}
                        onClick={() => {
                          handleClickCard(member);
                        }}
                      >
                        <Meta
                          title={member.name_cn}
                          description={(() => {
                            const middleSchool = formatSchoolInfo(member.schoolm_start_time, member.schoolm_class, '初中');
                            const highSchool = formatSchoolInfo(member.schoolh_start_time, member.schoolh_class, '高中');
                            
                            const parts = [middleSchool, highSchool].filter(Boolean);
                            return parts.length > 0 ? parts.join(' | ') : '校友信息';
                          })()}
                        />
                        <Divider />
                        <div className="alumni-info">
                          <div className="info-section">
                            <div className="info-label">高等教育</div>
                            <p className={`info-content ${isEducationInfoEmpty(member) ? 'empty' : ''}`}>
                              {formatEducationInfo(member)}
                            </p>
                          </div>
                          <div className="info-section">
                            <div className="info-label">职业信息</div>
                            <p className={`info-content ${isCareerInfoEmpty(member) ? 'empty' : ''}`}>
                              {formatCareerInfo(member)}
                            </p>
                          </div>
                        </div>
                      </Card>
                    );
                  })
                ) : (
                  <div className="no-results">
                    <Empty
                      description={loading ? '正在加载开发团队成员信息...' : '暂无开发团队成员信息'}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                )}
              </div>
            </Spin>
          </Card>

          <Card className="contact-card">
            <Title level={2} className="section-title">
              联系我们
            </Title>
            <Paragraph>
              如果您有任何建议、问题或希望为平台发展贡献力量，欢迎随时与我们联系。
            </Paragraph>
            <Space>
              <Text strong>邮箱：</Text>
              <Text copyable>
                <a href="mailto:edpalums@gmail.com" style={{ color: 'inherit', textDecoration: 'none' }}>
                  edpalums@gmail.com
                </a>
              </Text>
            </Space>
          </Card>
        </div>
      </div>

      {/* Modal for expanded profile cards */}
      <Modal
        open={expandedContact}
        footer={null}
        width="70vw"
        className="detailed-profile-modal"
        onCancel={() => {
          setExpandedContact(null);
        }}
      >
        <ProfileCard profile={expandedContact}/>
      </Modal>
    </MenuLayout>
  );
};

export default AboutPage;
